import { Button, Typography } from '@breetometer/components';
import { CopyIcon, HourGlassIcon, TopupIcon } from 'BreetAssets';
import { Amount } from 'BreetComponents';
import { copyToClipboard } from 'BreetHelpers';
import { useCountDown, useModalApi } from 'BreetHooks';
import { useEffect, useMemo } from 'react';

import type { ITopupAccountDetailsProps } from '../helpers';

const TopupAccountDetails = ({ setTopupStatus, amount, data }: ITopupAccountDetailsProps) => {
	const { handleModalClose } = useModalApi();

	const initialSeconds = useMemo(() => {
		if (!data?.expiryTime) return 0;
		const remaining = Math.floor((Date.parse(data.expiryTime) - Date.now()) / 1000);
		return Number.isNaN(remaining) ? 0 : Math.max(0, remaining);
	}, [data?.expiryTime]);

	const { countdown } = useCountDown(initialSeconds);

	useEffect(() => {
		if (countdown === 0) {
			setTopupStatus?.('expired')();
		}
	}, [countdown, setTopupStatus]);

	const formattedCountdown = `${String(Math.floor(countdown / 60)).padStart(2, '0')}:${String(countdown % 60).padStart(2, '0')}`;

	return (
		<div className='topup-account-details'>
			<div className='topup-account-header'>
				<TopupIcon className='header-icon' />
				<Typography className='color-neutral-50'>
					Transfer exactly{' '}
					<span>
						₵<Amount>{amount}</Amount>
					</span>{' '}
					to this account to top-up your Cedis wallet.
				</Typography>
			</div>

			<div className='new-drop-down-input'>
				<div className='topup-account-details bg-primary-500 border-dark-navy'>
					<div className='topup-account-details-item border-bottom-primary-400'>
						<div className='topup-account-details-item-left'>
							<Typography
								as='label'
								className='color-primary-100'
							>
								ACCOUNT NAME
							</Typography>
							<Typography className='color-neutral-50'>{data?.accountName}</Typography>
						</div>
						<CopyIcon
							variant='colored'
							aria-label='account name copy'
							role='button'
							tabIndex={0}
						/>
					</div>

					<div className='topup-account-details-item border-bottom-primary-400'>
						<div className='topup-account-details-item-left'>
							<Typography
								as='label'
								className='color-primary-100'
							>
								ACCOUNT NUMBER
							</Typography>
							<Typography className='color-neutral-50'>{data?.accountNumber}</Typography>
						</div>
						<CopyIcon
							variant='colored'
							onClick={copyToClipboard(data?.accountNumber, 'Account number copied')}
							aria-label='account number copy'
							role='button'
							tabIndex={0}
						/>
					</div>

					<div className='topup-account-details-item'>
						<div className='topup-account-details-item-left'>
							<Typography
								as='label'
								className='color-primary-100'
							>
								BANK NAME
							</Typography>
							<Typography className='color-neutral-50'>{data?.bankName}</Typography>
						</div>
						<CopyIcon
							variant='colored'
							aria-label='bank name copy'
							role='button'
							tabIndex={0}
						/>
					</div>
				</div>

				<div className='topup-account-info bg-primary-400 border-dark-navy'>
					<HourGlassIcon />
					<Typography className='color-tertiary-300'>
						This account is for this transaction only and expires in <br />
						<span>{formattedCountdown}</span>
					</Typography>
				</div>

				<div className='new-btn-cta'>
					<Button
						onClick={handleModalClose('topup')}
						className='tertiary-semi-hover bg-tertiary-400 color-neutral-50 bg-tertiary-500-hover'
						type='button'
					>
						I&apos;ve sent the money
					</Button>
				</div>
			</div>
		</div>
	);
};

export default TopupAccountDetails;
